
import React, { useMemo, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { ValuationEngine, CryptoPrice } from '../../engines/valuationEngine';
import { setValuationEngine } from '../onchain/onchainNormalizer';
import { normalizeStoreOnchainAssets } from '../onchain/storeOnchainAsset';
import { syncExchange } from '../../services/exchangeApi';
import { useExchangeStore } from '../../stores/exchangeStore';
import { usePortfolioStore } from '../../stores/portfolioStore';
import { getConnectionEntities } from '../../api/connections';
import { fetchAndSetCryptoTable } from '../../utils/fetchCryptoTable';
import { createSyncTimestamp, formatSyncDate } from '../../utils/syncDate';
import { ManualAssetModal } from './ManualAssetModal';
import { ManualAssetActions } from './ManualAssetActions';

const DEFAULT_EXCHANGE_ID_TO_NAME: Record<string, string> = {
  '1': 'mexc',
  '3': 'pionex',
  '4': 'gateio',
  '5': 'gateio',
  '6': 'bitrue',
};

const usdFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const percentFormat = new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 2 });
const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 });

const firstNonEmptyText = (...values: any[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const formatWalletAddress = (value: any): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  const normalized = value.trim();
  if (normalized.length <= 14) {
    return normalized;
  }
  return `${normalized.slice(0, 6)}...${normalized.slice(-4)}`;
};

const resolveSourceColumnLabel = (position: any, exchangeIdToNameMap: Record<string, string>): string => {
  if (Array.isArray(position?.sourceLabels) && position.sourceLabels.length > 0) {
    return position.sourceLabels.join(', ');
  }

  const sourceType = String(position?.sourceType ?? position?.type ?? '').toUpperCase();
  if (sourceType === 'ONCHAIN') {
    return firstNonEmptyText(
      position?.label,
      position?.walletLabel,
      position?.name,
      formatWalletAddress(position?.address),
    );
  }

  if (Array.isArray(position?.exchanges)) {
    return position.exchanges
      .map((exchangeId: any) => exchangeIdToNameMap[String(exchangeId)]?.toUpperCase() || String(exchangeId))
      .join(', ');
  }

  return '';
};

const buildAggregatedSource = (position: any, exchangeIdToNameMap: Record<string, string>) => {
  const sourceType = String(position?.sourceType ?? position?.type ?? '').toUpperCase();

  if (sourceType === 'ONCHAIN') {
    const walletId = firstNonEmptyText(String(position?.walletId ?? ''), String(position?.id ?? ''));
    const fallbackAddress = formatWalletAddress(position?.address);
    const sourceLabel = firstNonEmptyText(
      position?.label,
      position?.walletLabel,
      position?.name,
      fallbackAddress,
      'WALLET',
    );

    return {
      sourceKey: walletId ? `ONCHAIN:${walletId}` : `ONCHAIN:${sourceLabel}`,
      sourceLabel,
      normalizedType: 'ONCHAIN',
    };
  }

  const sourceId = String(position?.sourceId ?? '');
  const exchangeName = sourceId
    ? (exchangeIdToNameMap[sourceId] || sourceId).toUpperCase()
    : firstNonEmptyText(position?.name, 'EXCHANGE');

  return {
    sourceKey: sourceId ? `EXCHANGE:${sourceId}` : `EXCHANGE:${exchangeName}`,
    sourceLabel: exchangeName,
    normalizedType: 'EXCHANGE',
  };
};

declare global {
  interface Window {
    userCurrency?: string;
    userCurrencyRate?: number;
    userCurrencySymbol?: string;
    cryptoTable?: any[];
    cryptoPriceTable?: CryptoPrice[];
  }
}

const getRuntimeCryptoTable = (): CryptoPrice[] => {
  if (typeof window === 'undefined') return [];
  if (Array.isArray(window.cryptoTable)) return window.cryptoTable as CryptoPrice[];
  if (Array.isArray(window.cryptoPriceTable)) return window.cryptoPriceTable as CryptoPrice[];
  return [];
};

const enrichAssetWithPrice = (asset: any, engine: ValuationEngine) => {
  const quantity = typeof asset?.quantity === 'number' ? asset.quantity : 0;
  const normalizedAssetId = typeof asset?.assetId === 'string' ? asset.assetId.toUpperCase() : asset?.assetId;
  const sourceType = String(asset?.sourceType ?? asset?.type ?? '').toUpperCase();
  const existingPrice = typeof asset?.price === 'number' && asset.price > 0 ? asset.price : undefined;
  const fallbackPrice = typeof normalizedAssetId === 'string' ? engine.getPrice(normalizedAssetId) : 0;
  const runtimePrice = fallbackPrice > 0 ? fallbackPrice : undefined;
  const price = sourceType === 'MANUAL'
    ? (existingPrice ?? runtimePrice)
    : (runtimePrice ?? existingPrice);
  const existingValue = typeof asset?.value === 'number' && !Number.isNaN(asset.value) && asset.value > 0 ? asset.value : undefined;
  const value = typeof price === 'number'
    ? price * quantity
    : (existingValue ?? 0);

  return {
    ...asset,
    assetId: normalizedAssetId,
    price,
    value,
  };
};

export const UnifiedPortfolioTable: React.FC = () => {
    const [runtimeCryptoTable, setRuntimeCryptoTable] = useState<CryptoPrice[]>(() => getRuntimeCryptoTable());
    const [exchangeIdToNameMap, setExchangeIdToNameMap] = useState<Record<string, string>>(DEFAULT_EXCHANGE_ID_TO_NAME);
  const [expandedExchangeBreakdowns, setExpandedExchangeBreakdowns] = useState<Record<string, boolean>>({});

    React.useEffect(() => {
      let disposed = false;

      const loadExchangeIdMap = async () => {
        try {
          const entities = await getConnectionEntities();
          if (!Array.isArray(entities)) {
            return;
          }

          const nextMap: Record<string, string> = { ...DEFAULT_EXCHANGE_ID_TO_NAME };
          entities
            .filter((entity: any) => entity?.type === 'EXCHANGE' && entity?.id != null && entity?.name)
            .forEach((entity: any) => {
              nextMap[String(entity.id)] = String(entity.name).toLowerCase();
            });

          if (!disposed) {
            setExchangeIdToNameMap(nextMap);
          }
        } catch {
          // Keep fallback mapping when backend is unavailable.
        }
      };

      void loadExchangeIdMap();
      window.addEventListener('refresh-connection-entities', loadExchangeIdMap);

      return () => {
        disposed = true;
        window.removeEventListener('refresh-connection-entities', loadExchangeIdMap);
      };
    }, []);
    // Initialisation de ValuationEngine et injection dans onchainNormalizer
    React.useEffect(() => {
        const initializeValuationEngine = async () => {
          let cryptoTable = getRuntimeCryptoTable();
          if (cryptoTable.length === 0) {
            cryptoTable = await fetchAndSetCryptoTable();
          }
          setRuntimeCryptoTable(cryptoTable);
          const engine = new ValuationEngine(cryptoTable);
          setValuationEngine(engine);
        };

        initializeValuationEngine();
    }, []);
    const hideSmallAssetsKey = 'unified_portfolio_hide_small_assets';
    const [hideSmallAssets, setHideSmallAssets] = useState<boolean>(() => {
      const val = localStorage.getItem(hideSmallAssetsKey);
      return val === 'true';
    });
    const [localPassword, setLocalPassword] = useState('');
    const portfolioStore = usePortfolioStore();
    const exchanges = useExchangeStore(state => state.exchanges);
    const addManualPosition = usePortfolioStore(s => s.addManualPosition);
    const updateManualPosition = usePortfolioStore(s => s.updateManualPosition);
    const removeManualPosition = usePortfolioStore(s => s.removeManualPosition);
    const allocation = usePortfolioStore(s => s.allocation);
    const totalValue = usePortfolioStore(s => s.totalValue);
    const [modalOpen, setModalOpen] = useState(false);
    const [editManual, setEditManual] = useState(null as any);
    const [lastSync, setLastSync] = useState(() => {
      return localStorage.getItem('ecotoneai_lastSync') || new Date().toLocaleString();
    });
    const [syncSnackbarOpen, setSyncSnackbarOpen] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);
  // Utilise onchainAssets du store Zustand
  const rawOnchainAssets = usePortfolioStore(s => s.onchainAssets);
  const onchainAssets = useMemo(
    () => normalizeStoreOnchainAssets(Array.isArray(rawOnchainAssets) ? rawOnchainAssets : []),
    [rawOnchainAssets],
  );
  const onchainValued = useMemo(() => {
    const engine = new ValuationEngine(runtimeCryptoTable);
    return onchainAssets.map((asset: any) => enrichAssetWithPrice({ ...asset, sourceType: 'ONCHAIN', type: 'ONCHAIN' }, engine));
  }, [onchainAssets, runtimeCryptoTable]);
  const positions = useMemo(() => {
    const engine = new ValuationEngine(runtimeCryptoTable);
    const safePositions = Array.isArray(portfolioStore.positions) ? portfolioStore.positions : [];
    const safeManualPositions = Array.isArray(portfolioStore.manualPositions) ? portfolioStore.manualPositions : [];
    const exchangesFiltered = safePositions
      .filter((p: any) => p && p.sourceType !== 'MANUAL')
      .map((p: any) => enrichAssetWithPrice({ ...p, sourceType: p.sourceType || 'EXCHANGE' }, engine));
    const manualValued = safeManualPositions.map((manual: any) => enrichAssetWithPrice({ ...manual, type: 'MANUAL' }, engine));
    return [...exchangesFiltered, ...manualValued, ...onchainValued];
  }, [portfolioStore.positions, portfolioStore.manualPositions, onchainValued, runtimeCryptoTable]);
  const handleSync = async () => {
    if (!localPassword) {
      alert('Veuillez saisir votre mot de passe local unique pour synchroniser.');
      return;
    }
    try {
      const allAssets: any[] = [];
      let cryptoTable = getRuntimeCryptoTable();
      if (cryptoTable.length === 0) {
        cryptoTable = await fetchAndSetCryptoTable();
      }
      setRuntimeCryptoTable(cryptoTable);
      const engine = new ValuationEngine(cryptoTable);
      for (const exchange of Object.keys(exchanges)) {
        if (exchanges[exchange] === 'CONNECTED') {
          let credentials: any = {};
          try {
            const encrypted = localStorage.getItem(`ecotoneai_${exchange}_credentials`);
            if (encrypted) {
              credentials = { encrypted, secretKey: localPassword };
            }
          } catch (e) {
            console.warn('Erreur récupération credentials pour', exchange, e);
          }
          let result;
          try {
            result = await syncExchange({ exchange, credentials });
          } catch (err) {
            if (err instanceof Error && err.message && err.message.includes('Sync temporarily unavailable')) {
              setSyncError('Service de synchronisation temporairement indisponible. Merci de réessayer plus tard.');
            } else {
              alert('Erreur réseau ou API : ' + (err instanceof Error ? err.message : JSON.stringify(err)));
            }
            return;
          }
          if (result && (result.status === 'NOTOK' || result.result === 'NOTOK' || result.error)) {
            if ((result.error || result.message || '').includes('Sync temporarily unavailable')) {
              setSyncError('Service de synchronisation temporairement indisponible. Merci de réessayer plus tard.');
            } else {
              alert('Erreur API : ' + (result.error || result.message || JSON.stringify(result)));
            }
            return;
          }
          const positions = (result && Array.isArray(result.assets)) ? result.assets : (result && Array.isArray(result.positions)) ? result.positions : [];
          const positionsWithSource = positions.map((asset: any) => enrichAssetWithPrice({ ...asset, sourceId: exchange }, engine));
          allAssets.push(...positionsWithSource);
        }
      }
      usePortfolioStore.getState().applySyncResult(allAssets);
      const syncTime = createSyncTimestamp();
      setLastSync(syncTime);
      localStorage.setItem('ecotoneai_lastSync', syncTime);
      window.dispatchEvent(new Event('ecotoneai_lastSync_updated'));
      setSyncSnackbarOpen(true);
    } catch (e) {
      alert('Erreur inattendue lors de la synchronisation : ' + (e instanceof Error ? e.message : JSON.stringify(e)));
    }
  };

  // Agrégation par assetId pour l'affichage
  const aggregated = useMemo(() => {
    // Sépare les assets manuels des sources synchronisées (exchanges + wallets).
    const manualAssets = positions.filter(pos => pos.sourceType === 'MANUAL');
    const aggregatedSyncedAssets: any[] = [];
    const map = new Map<string, any>();
    positions.filter(pos => pos.sourceType !== 'MANUAL').forEach(pos => {
      // Pour chaque assetId, on regroupe toutes les sources (exchange + onchain).
      if (!map.has(pos.assetId)) {
        map.set(pos.assetId, {
          ...pos,
          sourceLabels: [],
          sourceBreakdown: {},
          hasExchangeSource: false,
          hasOnchainSource: false,
          quantity: 0,
          value: 0,
          price: typeof pos.price === 'number' ? pos.price : undefined,
          type: 'EXCHANGE',
          source: pos.sourceType || 'exchange',
        });
      }
      const agg = map.get(pos.assetId);
      const { sourceKey, sourceLabel, normalizedType } = buildAggregatedSource(pos, exchangeIdToNameMap);
      agg.quantity += typeof pos.quantity === 'number' ? pos.quantity : 0;
      // Si la position a une valeur, on additionne, sinon on calcule value = price * quantity
      let val = typeof pos.value === 'number' ? pos.value : (typeof pos.price === 'number' && typeof pos.quantity === 'number' ? pos.price * pos.quantity : 0);
      agg.value += val;

      if (sourceKey) {
        const existingBreakdown = agg.sourceBreakdown[sourceKey] || { quantity: 0, value: 0, label: sourceLabel };
        agg.sourceBreakdown[sourceKey] = {
          quantity: existingBreakdown.quantity + (typeof pos.quantity === 'number' ? pos.quantity : 0),
          value: existingBreakdown.value + val,
          label: existingBreakdown.label || sourceLabel,
        };

        if (!agg.sourceLabels.includes(sourceLabel)) {
          agg.sourceLabels.push(sourceLabel);
        }
      }

      agg.hasExchangeSource = agg.hasExchangeSource || normalizedType === 'EXCHANGE';
      agg.hasOnchainSource = agg.hasOnchainSource || normalizedType === 'ONCHAIN';

      // Si la position a un prix, on garde le dernier prix non nul rencontré
      if (typeof pos.price === 'number' && pos.price > 0) agg.price = pos.price;
    });

    Array.from(map.values()).forEach((asset) => {
      asset.type = asset.hasExchangeSource && asset.hasOnchainSource
        ? 'MIXED'
        : (asset.hasOnchainSource ? 'ONCHAIN' : 'EXCHANGE');
    });

    // Calcul allocation (%)
    const totalValue = Array.from(map.values()).reduce((sum, a) => sum + (a.value ?? 0), 0);
    Array.from(map.values()).forEach(a => {
      if (!(typeof a.price === 'number' && a.price > 0) && typeof a.quantity === 'number' && a.quantity > 0 && typeof a.value === 'number' && a.value > 0) {
        a.price = a.value / a.quantity;
      }
      a.allocation = totalValue > 0 ? (a.value ?? 0) / totalValue : 0;
    });
    aggregatedSyncedAssets.push(...Array.from(map.values()));
    // Les assets manuels sont affichés individuellement, avec type MANUAL
    const manualAssetsWithType = manualAssets.map(a => {
      let value = typeof a.value === 'number' ? a.value : (typeof a.price === 'number' && typeof a.quantity === 'number' ? a.price * a.quantity : 0);
      let price = typeof a.price === 'number' ? a.price : (typeof value === 'number' && typeof a.quantity === 'number' && a.quantity !== 0 ? value / a.quantity : 0);
      return { ...a, type: 'MANUAL', value, price, source: 'manual', exchanges: [] };
    });

    const combined = [...aggregatedSyncedAssets, ...manualAssetsWithType];
    const combinedTotalValue = combined.reduce((sum, asset) => sum + (typeof asset.value === 'number' ? asset.value : 0), 0);
    combined.forEach((asset) => {
      asset.allocation = combinedTotalValue > 0 ? ((asset.value ?? 0) / combinedTotalValue) : 0;
    });
    return combined.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }, [positions, exchangeIdToNameMap]);
  const aggregatedTotalValue = useMemo(
    () => aggregated.reduce((sum, pos) => sum + (typeof pos.value === 'number' ? pos.value : 0), 0),
    [aggregated]
  );
  const visibleAggregated = useMemo(
    () => aggregated.filter((pos) => !hideSmallAssets || (pos.value ?? 0) >= 0.01),
    [aggregated, hideSmallAssets]
  );

  const controlInputStyle: React.CSSProperties = {
    padding: '12px 14px',
    borderRadius: 14,
    border: '1px solid rgba(16,37,43,0.12)',
    fontSize: 15,
    width: 340,
    minWidth: 240,
    background: '#fffdf8',
    color: '#17323c',
  };

  const primaryButtonStyle: React.CSSProperties = {
    padding: '12px 18px',
    borderRadius: 999,
    background: '#17323c',
    color: '#fff8ef',
    border: 'none',
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
    boxShadow: '0 10px 26px rgba(16,37,43,0.16)',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...primaryButtonStyle,
    background: '#214f77',
  };

  const metricPillStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderRadius: 16,
    background: '#fffdf8',
    border: '1px solid rgba(16,37,43,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 128,
  };

  // ...existing code...

  const handleHideSmallAssetsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHideSmallAssets(e.target.checked);
    localStorage.setItem(hideSmallAssetsKey, e.target.checked ? 'true' : 'false');
  };

  const toggleExchangeBreakdown = (assetId: string) => {
    setExpandedExchangeBreakdowns((current) => ({
      ...current,
      [assetId]: !current[assetId],
    }));
  };

  const resolveSourceName = (sourceKey: string, breakdown: { label?: string }) => {
    if (breakdown?.label) {
      return breakdown.label;
    }

    const [sourceType, sourceId] = String(sourceKey || '').split(':');
    if (sourceType === 'EXCHANGE') {
      return (exchangeIdToNameMap[String(sourceId)] || String(sourceId)).toUpperCase();
    }
    return String(sourceId || sourceKey);
  };
  // UI
  return (
    <>
    <Snackbar
      open={syncSnackbarOpen}
      autoHideDuration={3500}
      onClose={() => setSyncSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MuiAlert onClose={() => setSyncSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
        Synchronisation terminée !
      </MuiAlert>
    </Snackbar>
    <Snackbar
      open={!!syncError}
      autoHideDuration={6000}
      onClose={() => setSyncError(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MuiAlert onClose={() => setSyncError(null)} severity="error" sx={{ width: '100%' }}>
        {syncError}
      </MuiAlert>
    </Snackbar>
    <div style={{ background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)', borderRadius: 28, boxShadow: '0 18px 50px rgba(16,37,43,0.08)', border: '1px solid rgba(16,37,43,0.08)', padding: 28, margin: 'auto', maxWidth: 1280 }}>
      <div style={{ display: 'grid', gap: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, flex: 1 }}>
        <input
          type="password"
          placeholder="Mot de passe local unique (chiffrement)"
          value={localPassword}
          onChange={e => setLocalPassword(e.target.value)}
          style={controlInputStyle}
        />
        <button
          onClick={handleSync}
          style={primaryButtonStyle}
        >
          Synchroniser le portefeuille
        </button>
        <button
          onClick={() => { setEditManual(null); setModalOpen(true); }}
          style={secondaryButtonStyle}
        >
          + Ajouter Asset Manuellement
        </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <div style={metricPillStyle}>
              <span style={{ fontSize: 12, color: '#6d7e84', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Lignes visibles</span>
              <span style={{ fontSize: 18, color: '#17323c', fontWeight: 800 }}>{visibleAggregated.length}</span>
            </div>
            <div style={metricPillStyle}>
              <span style={{ fontSize: 12, color: '#6d7e84', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Valeur agrégée</span>
              <span style={{ fontSize: 18, color: '#17323c', fontWeight: 800 }}>{usdFormat.format(aggregatedTotalValue || totalValue)}</span>
            </div>
            <div style={metricPillStyle}>
              <span style={{ fontSize: 12, color: '#6d7e84', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sources chargées</span>
              <span style={{ fontSize: 18, color: '#17323c', fontWeight: 800 }}>{positions.length}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderRadius: 20, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="hideSmallAssets"
            checked={hideSmallAssets}
            onChange={handleHideSmallAssetsChange}
            style={{ marginRight: 8 }}
          />
          <label htmlFor="hideSmallAssets" style={{ fontSize: 15, color: '#17323c', userSelect: 'none', fontWeight: 600 }}>
            Cacher les assets dont la Valeur Totale &lt; $0.01
          </label>
        </div>
          <div style={{ fontSize: 15, color: '#556970', textAlign: 'right', minWidth: 220 }}>
            <span style={{ fontWeight: 700, color: '#17323c' }}>Dernière synchronisation : </span>{formatSyncDate(lastSync)}
          </div>
        </div>
      </div>
      <div style={{ overflowX: 'auto', borderRadius: 22, border: '1px solid rgba(16,37,43,0.08)', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#fff' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#17323c', zIndex: 2 }}>
            <tr>
              <th style={{ padding: '14px 12px', textAlign: 'left', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', borderTopLeftRadius: 22, color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Asset</th>
              <th style={{ padding: '14px 12px', textAlign: 'right', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Quantité Totale</th>
              <th style={{ padding: '14px 12px', textAlign: 'left', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Sources</th>
              <th style={{ padding: '14px 12px', textAlign: 'right', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Prix (USD)</th>
              <th style={{ padding: '14px 12px', textAlign: 'right', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Valeur (USD)</th>
              <th style={{ padding: '14px 12px', textAlign: 'right', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Allocation (%)</th>
              <th style={{ padding: '14px 12px', textAlign: 'center', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Type</th>
              <th style={{ padding: '14px 12px', textAlign: 'center', borderBottom: '1px solid rgba(248,244,236,0.16)', background: '#17323c', borderTopRightRadius: 22, color: '#fff8ef', fontWeight: 700, fontSize: 15 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleAggregated.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 36, color: '#6b7b82' }}>Aucun actif visible dans le portefeuille avec les filtres actuels.</td>
              </tr>
            )}
            {visibleAggregated.map((pos, idx) => {
              const isAggregatedLine = pos.type !== 'MANUAL';
              const sourceBreakdownEntries = isAggregatedLine && pos.sourceBreakdown
                ? Object.entries(pos.sourceBreakdown as Record<string, { quantity: number; value: number; label?: string }>)
                : [];
              const sortedSourceBreakdownEntries = sourceBreakdownEntries
                .slice()
                .sort((a, b) => (b[1]?.value || 0) - (a[1]?.value || 0));
              const canExpandBreakdown = sourceBreakdownEntries.length > 1;
              const isBreakdownExpanded = Boolean(expandedExchangeBreakdowns[String(pos.assetId)]);

              return (
                <tr
                  key={pos.type === 'MANUAL' && pos.id ? `manual-${pos.id}-${idx}` : pos.assetId + '-' + idx}
                  style={{ background: idx % 2 === 0 ? '#fcfaf4' : '#fff', transition: 'background 0.2s', cursor: 'pointer', borderBottom: '1px solid rgba(16,37,43,0.05)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f5efe2')}
                  onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#fcfaf4' : '#fff')}
                >
                  <td style={{ padding: '12px', fontWeight: 700, color: '#17323c', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {pos.assetId}
                    {pos.type === 'MANUAL' && <span style={{ background: 'rgba(33,79,119,0.08)', color: '#214f77', borderRadius: 999, fontSize: 12, padding: '4px 10px', marginLeft: 6, fontWeight: 800 }}>MANUAL</span>}
                  </td>
                  <td style={{ padding: '12px', fontWeight: 700, color: '#17323c', fontSize: 15, textAlign: 'right' }}>{numberFormat.format(pos.quantity)}</td>
                  <td style={{ padding: '12px', color: '#214f77', fontSize: 14, fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>{resolveSourceColumnLabel(pos, exchangeIdToNameMap)}</span>
                      {canExpandBreakdown ? (
                        <button
                          type="button"
                          onClick={() => toggleExchangeBreakdown(String(pos.assetId))}
                          style={{
                            border: '1px solid rgba(23,50,60,0.18)',
                            background: '#fffdf8',
                            color: '#17323c',
                            borderRadius: 999,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {isBreakdownExpanded ? 'Masquer détail' : 'Voir détail'}
                        </button>
                      ) : null}
                    </div>
                    {isBreakdownExpanded && canExpandBreakdown ? (
                      <div style={{ marginTop: 8, display: 'grid', gap: 4, fontSize: 12, color: '#17323c' }}>
                        {sortedSourceBreakdownEntries.map(([sourceKey, breakdown]) => (
                          <div key={`${pos.assetId}-${sourceKey}`}>
                            {resolveSourceName(sourceKey, breakdown)}: {numberFormat.format(breakdown?.quantity || 0)}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ padding: '12px', fontWeight: 700, color: '#17323c', fontSize: 15, textAlign: 'right' }}>
                    {usdFormat.format(
                      (typeof pos.price === 'number' && pos.price > 0)
                        ? pos.price
                        : ((typeof pos.value === 'number' && typeof pos.quantity === 'number' && pos.quantity > 0)
                            ? (pos.value / pos.quantity)
                            : 0)
                    )}
                  </td>
                  <td style={{ padding: '12px', fontWeight: 700, color: '#17323c', fontSize: 15, textAlign: 'right' }}>
                    {usdFormat.format(pos.value ?? 0)}
                    {isBreakdownExpanded && canExpandBreakdown ? (
                      <div style={{ marginTop: 8, display: 'grid', gap: 4, fontSize: 12, color: '#17323c', fontWeight: 600 }}>
                        {sortedSourceBreakdownEntries.map(([sourceKey, breakdown]) => (
                          <div key={`${pos.assetId}-${sourceKey}-value`}>
                            {resolveSourceName(sourceKey, breakdown)}: {usdFormat.format(breakdown?.value || 0)}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ padding: '12px', fontWeight: 700, color: '#17323c', fontSize: 15, textAlign: 'right' }}>
                    {percentFormat.format(
                      typeof pos.allocation === 'number'
                        ? pos.allocation
                        : ((allocation.find((a: any) => a.assetId === pos.assetId)?.percent || 0) / 100)
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: 700, color: '#214f77', fontSize: 13 }}>
                    {pos.type === 'MANUAL' ? 'MANUAL' : pos.type === 'MIXED' ? 'MIXED' : pos.type === 'ONCHAIN' ? 'ONCHAIN' : 'EXCHANGE'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {pos.type === 'MANUAL' && pos.id ? (
                      <ManualAssetActions
                        onEdit={() => { setEditManual(pos); setModalOpen(true); }}
                        onDelete={() => removeManualPosition(pos.id)}
                      />
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {aggregated.length > 0 && (
              <tr style={{ background: '#f2ecde', fontWeight: 700 }}>
                <td colSpan={4} style={{ textAlign: 'right', padding: '14px 12px', color: '#17323c' }}>TOTAL</td>
                <td style={{ padding: '14px 12px', textAlign: 'right', color: '#17323c' }}>{usdFormat.format(aggregatedTotalValue || totalValue)}</td>
                <td colSpan={3}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ManualAssetModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={pos => {
          if (editManual) updateManualPosition(pos); else addManualPosition(pos);
          setModalOpen(false);
        }}
        initial={editManual}
      />
    </div>
    </>
  );
};
